import type { ComponentType, SVGProps } from "react";

import { builderImageUrl } from "~/hec/utils/builder-image-url";

export type IconComponentMap = Record<
  string,
  ComponentType<SVGProps<SVGSVGElement>>
>;

interface CustomIconReference {
  id: string;
  value?: {
    data?: {
      iconImage?: string;
      [key: string]: unknown;
    };
  };
}

export interface IconPickerProps {
  iconName?: string;
  attributes?: Record<string, unknown>;
  useReference?: boolean;
  CustomIcon?: CustomIconReference;
  useBuilderImageAttributes?: boolean;
}

/**
 * Shared IconPicker renderer, parameterized by its icon map so the two
 * registrations can ship different graphs: the full registry binds the whole
 * icon set (route-chunked with the page registry), while the chrome registry
 * binds only the audited chrome subset — keeping the ~113-icon map out of the
 * always-loaded layout chunk (COM-188). This module must not import any icon.
 *
 * Rendering is deterministic from (map, props) and identical on server and
 * client, so a map miss cannot cause a hydration mismatch — both sides render
 * the same fallback span.
 */
export const IconPickerBase = ({
  icons,
  onUnknownIcon,
  iconName,
  attributes,
  useReference,
  CustomIcon,
  useBuilderImageAttributes,
}: IconPickerProps & {
  icons: IconComponentMap;
  onUnknownIcon?: (iconName: string) => void;
}) => {
  // When using a Builder reference to a custom icon
  if (useReference && CustomIcon) {
    const iconSrc = CustomIcon?.value?.data?.iconImage || "";
    return (
      <picture>
        <img
          loading="lazy"
          fetchPriority="auto"
          role="presentation"
          {...(attributes as React.ImgHTMLAttributes<HTMLImageElement>)}
          className={
            !useBuilderImageAttributes
              ? "h-6 w-6"
              : (attributes?.className as string) || undefined
          }
          src={builderImageUrl(iconSrc, { width: 48, format: "webp" })}
          alt="Custom Icon"
          data-testid="icon-picker-CustomIconComponent"
          sizes="(max-width: 998px) 2vw, 2vw"
        />
      </picture>
    );
  }

  if (!iconName) return null;

  const IconComponent = icons[iconName];
  if (IconComponent) {
    const { className: attrClassName, ...restAttributes } = (attributes ||
      {}) as Record<string, unknown>;
    return (
      <IconComponent
        className={(attrClassName as string) || "h-5 w-5"}
        {...(restAttributes as SVGProps<SVGSVGElement>)}
        data-testid="icon-picker-IconComponent"
        aria-hidden="true"
      />
    );
  }

  onUnknownIcon?.(iconName);

  // Fallback for unrecognized icon names
  return (
    <span
      {...(attributes as React.HTMLAttributes<HTMLSpanElement>)}
      data-icon-name={iconName}
      data-testid="icon-picker-IconComponent"
      aria-hidden="true"
    />
  );
};
